import { memo, useState, useCallback, useRef } from 'react'
import { UploadArea } from 'components/UploadArea'

import AddIcon from 'assets/svgr-icons/Add.svg'
import UploadIcon from 'assets/svgr-icons/Upload.svg'
import MyFilesIcon from 'assets/svgr-icons/My_Files.svg'
import ActionsFlyout from 'common-components/ActionsFlyout'
import MediaUploadButton from 'components/MediaUploadButton'
import { useDropdownState } from 'hooks/useDropdownState'
import { useMobileInnerSliderCallbacks } from 'hooks/useMobileInnerSliderCallbacks'
import { useScrollableElement } from 'containers/SubSidebar/AIVideoContent/context'
import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'
import { useMultiRefUpload } from './useMultiRefUpload'
import * as Styled from './styled'
import MultiRefImageItem from './ImageItem'
import { openMediaLib } from 'utils/media'

const MultiRefImagesContent = () => {
  const {
    multiRefImagesBucket,
    availableCount,
    isUploading,
    handleFilesUpload,
    handleMediaLibFiles,
    handleRemoveImage
  } = useMultiRefUpload()

  const [addBtnEl, setAddBtnEl] = useState<HTMLDivElement | null>(null)
  const uploadTriggerRef = useRef<{ triggerUpload: () => void } | null>(null)

  const { scrollableElementRef } = useScrollableElement()
  const { openCallback, closeCallback } = useMobileInnerSliderCallbacks()
  const { isOpen, toggleDropdown, closeDropdown } = useDropdownState(
    openCallback,
    closeCallback
  )

  const handleUploadClick = useCallback(() => {
    uploadTriggerRef.current?.triggerUpload()
    closeDropdown()
  }, [closeDropdown])

  const flyoutActions = [
    {
      key: 'upload',
      title: translate(T.UPLOAD_LABEL),
      icon: <UploadIcon />,
      onClick: handleUploadClick
    },
    {
      key: 'my_files',
      title: translate(T.MY_FILES_LABEL),
      icon: <MyFilesIcon />,
      onClick: () => {
        openMediaLib({
          onChoose: handleMediaLibFiles,
          maxFileImportCount: availableCount
        })
      }
    }
  ]

  const hasImages = multiRefImagesBucket.some(img => img.url || img.isLoading)

  if (!hasImages) {
    return (
      <UploadArea
        handleFilesUpload={handleFilesUpload}
        acceptType="AIVideoImage"
        mediaLibConfig={{
          fileType: 'image',
          maxFileImportCount: availableCount
        }}
        handleMediaLibFiles={handleMediaLibFiles}
        isLoading={isUploading}
        multiple
      />
    )
  }

  return (
    <>
      <Styled.ImagesGrid>
        {availableCount > 0 && (
          <Styled.ImageOuterContainer
            onClick={toggleDropdown}
            ref={setAddBtnEl}
          >
            <Styled.AddButton $isToggled={isOpen}>
              <AddIcon />
            </Styled.AddButton>
          </Styled.ImageOuterContainer>
        )}
        {multiRefImagesBucket.map(item => (
          <MultiRefImageItem
            key={item.id}
            item={item}
            onRemove={handleRemoveImage}
          />
        ))}
      </Styled.ImagesGrid>

      <MediaUploadButton
        acceptType="AIVideoImage"
        handleFilesUpload={handleFilesUpload}
        refTrigger={uploadTriggerRef}
      />

      <ActionsFlyout
        actions={flyoutActions}
        isOpen={isOpen}
        anchorEl={addBtnEl}
        onClose={closeDropdown}
        flyoutOptions={{
          position: 'right',
          alignmentY: 'top',
          extraOffset: 4
        }}
        isMobileWithDrawer
        scrollableElementRef={scrollableElementRef}
      />
    </>
  )
}

export default memo(MultiRefImagesContent)
